import React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';

import Services from './Services';
import Fleet from './Fleet';
import Bookings from './Bookings';
import Settings from './Settings';

const Dashboard = () => {
  const [stats, setStats] = React.useState({ total: 0, pending: 0, delivered: 0 });

  React.useEffect(() => {
    const fetch = async () => {
      const { supabase } = await import('../../lib/supabase');
      const { data } = await supabase.from('bookings').select('status');
      if (data) {
        setStats({
          total: data.length,
          pending: data.filter((b) => b.status === 'pending_assignment').length,
          delivered: data.filter((b) => b.status === 'delivered').length,
        });
      }
    };
    fetch();
  }, []);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-brand-navy mb-6">Admin Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-gray-500 text-sm mb-1 uppercase tracking-wider">Total Bookings</p>
          <p className="text-3xl font-bold text-brand-navy">{stats.total}</p>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-gray-500 text-sm mb-1 uppercase tracking-wider">Pending Assignment</p>
          <p className="text-3xl font-bold text-yellow-500">{stats.pending}</p>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-gray-500 text-sm mb-1 uppercase tracking-wider">Delivered</p>
          <p className="text-3xl font-bold text-green-500">{stats.delivered}</p>
        </div>
      </div>
    </div>
  );
};

export default function AdminApp() {
  const { signOut } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();

  const menu = [
    { path: '/admin', icon: 'PieChart', label: 'Dashboard' },
    { path: '/admin/bookings', icon: 'BookOpen', label: 'Bookings' },
    { path: '/admin/fleet', icon: 'Truck', label: 'Fleet' },
    { path: '/admin/services', icon: 'List', label: 'Services' },
    { path: '/admin/settings', icon: 'Settings', label: 'Settings' },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-brand-navy text-white flex flex-col shadow-2xl z-10">
        <div className="p-6 flex items-center gap-3 border-b border-white/10">
          <div className="h-8 w-8 shrink-0 flex items-center justify-center bg-white/10 rounded-lg overflow-hidden">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-full w-full object-cover" />
            ) : (
              <SafeIcon name={branding.logo_icon || 'Droplet'} className="text-brand-gold text-xl" />
            )}
          </div>
          <span className="font-bold text-lg tracking-tight flex flex-col leading-tight">
            <span className="truncate max-w-[130px]">{branding.app_name.toUpperCase()}</span>
            <span className="text-brand-gold text-xs">ADMIN PANEL</span>
          </span>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menu.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                isActive(item.path)
                  ? 'bg-brand-gold text-white shadow-lg translate-x-1'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <SafeIcon name={item.icon} />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-red-400 transition-colors"
          >
            <SafeIcon name="LogOut" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-[#F9FAFB]">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/fleet" element={<Fleet />} />
          <Route path="/services" element={<Services />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </div>
    </div>
  );
}